import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, formatApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Loader2, Rocket } from 'lucide-react';

export function ClientClimaraLaunch({ onLaunched }) {
  const { i18n } = useTranslation();
  const isES = (i18n.language || 'es').startsWith('es');
  const L = (es, en) => (isES ? es : en);
  const [open, setOpen] = useState(false);
  const [lib, setLib] = useState([]);
  const [people, setPeople] = useState([]);
  const [form, setForm] = useState({ name: '', wave_label: '', questionnaire_id: '', deadline: '' });
  const [selUsers, setSelUsers] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      api.get('/client/library', { params: { suite: 'climara' } }).then((r) => setLib(r.data || [])).catch(() => {});
      api.get('/client/users').then((r) => setPeople((r.data || []).filter((u) => u.status !== 'inactive' && (u.employment_status || 'active') !== 'former'))).catch(() => {});
    }
  }, [open]);

  const launch = async () => {
    if (!form.name.trim() || !form.wave_label.trim() || !form.questionnaire_id || selUsers.length === 0) {
      toast.error(L('Complete nombre, ola, instrumento y participantes.', 'Complete name, wave, instrument and participants.'));
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post('/client/climara/campaigns', {
        name: form.name.trim(), wave_label: form.wave_label.trim(),
        questionnaire_id: form.questionnaire_id, deadline: form.deadline || null,
        department_ids: [], invited_user_ids: selUsers,
      });
      toast.success(L(`Campaña lanzada — ${data.assignments} invitaciones enviadas.`,
        `Campaign launched — ${data.assignments} invitations sent.`));
      setOpen(false); setSelUsers([]); setForm({ name: '', wave_label: '', questionnaire_id: '', deadline: '' });
      onLaunched && onLaunched();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <>
      <Button size="sm" onClick={() => setOpen(true)} data-testid="climara-self-launch-btn" className="gap-1.5">
        <Rocket className="h-4 w-4" /> {L('Lanzar campaña (autogestión)', 'Launch campaign (self-service)')}
      </Button>
      <Dialog open={open} onOpenChange={(v) => !busy && setOpen(v)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{L('Nueva campaña de clima — autogestión', 'New climate campaign — self-service')}</DialogTitle>
            <DialogDescription>
              {L('Lance directamente con instrumentos aprobados de CTH. La metodología y la calificación permanecen gobernadas por CTH.',
                 'Launch directly using approved CTH instruments. Methodology and scoring remain CTH-governed.')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div><Label>{L('Nombre', 'Name')}</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="climara-name" /></div>
              <div><Label>{L('Ola / período', 'Wave / period')}</Label>
                <Input value={form.wave_label} onChange={(e) => setForm({ ...form, wave_label: e.target.value })}
                       placeholder="2026-H1" data-testid="climara-wave" /></div>
            </div>
            <div><Label>{L('Instrumento aprobado', 'Approved instrument')}</Label>
              <Select value={form.questionnaire_id} onValueChange={(v) => setForm({ ...form, questionnaire_id: v })}>
                <SelectTrigger data-testid="climara-instrument"><SelectValue placeholder={L('Seleccionar…', 'Select…')} /></SelectTrigger>
                <SelectContent>{lib.map((x) => <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label>{L('Fecha límite (opcional)', 'Deadline (optional)')}</Label>
              <Input type="date" value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })}
                     data-testid="climara-deadline" /></div>
            <div>
              <div className="flex items-center justify-between">
                <Label>{L('Participantes', 'Participants')} ({selUsers.length})</Label>
                <button type="button" className="text-xs text-primary underline"
                        onClick={() => setSelUsers(selUsers.length === people.length ? [] : people.map((p) => p.id))}
                        data-testid="climara-select-all">
                  {selUsers.length === people.length ? L('Ninguno', 'None') : L('Todos', 'All')}
                </button>
              </div>
              <div className="max-h-40 overflow-y-auto rounded-md border border-border p-2 space-y-1" data-testid="climara-participants">
                {people.map((p) => (
                  <label key={p.id} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={selUsers.includes(p.id)}
                           onChange={() => setSelUsers((s) => s.includes(p.id) ? s.filter((x) => x !== p.id) : [...s, p.id])} />
                    <span className="truncate">{p.name} <span className="text-xs text-muted-foreground">({p.email})</span></span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={busy}>{L('Cancelar', 'Cancel')}</Button>
            <Button onClick={launch} disabled={busy} data-testid="climara-launch-submit">
              {busy && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
              {L('Lanzar campaña', 'Launch campaign')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default ClientClimaraLaunch;
